import { Editor, Node, Path, Range, Element as SlateElement, Transforms } from 'slate'

import { behaviorForTable } from '../helpers/deleteBackwardHelpers/behaviorForTable'
import { handleActionBlock } from '../helpers/deleteBackwardHelpers/handleActionBlock'
import { unwrapBlocks } from '../helpers/deleteBackwardHelpers/unwrapBlocks'
import { deleteForwardHelper } from '../helpers/deleteForwardHelpers/deleteForwardHelper'
import { deleteInsideExpandBlock } from '../helpers/deleteForwardHelpers/deleteInsideExpandBlock'
import { setColorMark, updateExpandBlock } from '../helpers/slateHelper'
import {
  BLOCK_FORMATS,
  CustomEditor,
  TCustomElement,
  TExpandBlockTitle,
} from '../interface/custom-types'

import { removeExpandBlock } from './../helpers/deleteBackwardHelpers/removeExpandBlock'

const isChildrenNotEmpty = (node: Node) => {
  return Array.isArray(node['children']) && node['children'].length > 0
}

export const withCorrectVoidBehavior = (editor: CustomEditor) => {
  const { deleteBackward, deleteForward, insertBreak, normalizeNode, isVoid } = editor

  editor.isVoid = (element) => {
    return element.type === BLOCK_FORMATS.separator ? true : isVoid(element)
  }

  editor.normalizeNode = (entry) => {
    const [node, path] = entry

    if (!editor.children.length) {
      Transforms.insertNodes(editor, {
        type: BLOCK_FORMATS.paragraph,
        children: [{ text: '' }],
      })
    }

    //if the text is empty then there is no point in saving styles for it,
    //because when the text is deleted, the styles will remain applied to the new text
    if (!Node.string(node) && (node['color'] || node['backgroundColor'])) {
      setColorMark(editor, 'color', undefined)
      setColorMark(editor, 'backgroundColor', undefined)
    }

    const isNeedNormalize =
      SlateElement.isElement(node) && (node.type === BLOCK_FORMATS.figure || editor.isVoid(node))

    if (isNeedNormalize) {
      if (node.type === BLOCK_FORMATS.figure && !isChildrenNotEmpty(node)) {
        Transforms.setNodes(
          editor,
          {
            type: BLOCK_FORMATS.figure,
            children: [{ text: '' }],
          },
          { at: path }
        )
      } else {
        // Add a line to the end so that you can continue editing the article
        const nextNodePath = Editor.after(editor, path)
        if (!nextNodePath && node.type !== BLOCK_FORMATS.figure) {
          Transforms.insertNodes(
            editor,
            {
              type: BLOCK_FORMATS.paragraph,
              children: [{ text: '' }],
            },
            { at: [editor.children.length] }
          )
        }
      }
    }

    normalizeNode(entry)
  }

  // if current selection is void node, insert a default node below
  editor.insertBreak = () => {
    if (!editor.selection || !Range.isCollapsed(editor.selection)) {
      return insertBreak()
    }

    const selectedNodePath = Path.parent(editor.selection.anchor.path)
    const selectedNode = Node.get(editor, selectedNodePath) as TCustomElement
    if (Editor.isVoid(editor, selectedNode)) {
      Editor.insertNode(editor, {
        type: BLOCK_FORMATS.paragraph,
        children: [{ text: '' }],
      })
      return
    }

    insertBreak()
  }

  editor.deleteForward = (unit) => {
    if (!editor.selection || !Range.isCollapsed(editor.selection)) {
      return deleteBackward(unit)
    }

    // Prohibits the use of delete Forward if the node in which the deletion occurs is the
    // last node in the cell and selection is at the end of this node, to avoid deleting next table cells
    const isInTableCell = Editor.above(editor, {
      match: (node) => SlateElement.isElement(node) && node.type === BLOCK_FORMATS.tableCell,
    })
    if (isInTableCell) {
      const currentNodeStringLength = Editor.string(editor, editor.selection.anchor.path).length
      const nextNodePath = Path.next(Path.parent(editor.selection.anchor.path))
      const isNextNodeExist = Node.has(editor, nextNodePath)
      if (!isNextNodeExist) {
        if (currentNodeStringLength === editor.selection.anchor.offset) {
          return
        }
      }
    }

    // we disable the use of deleteForward in cases where we are at the end of a string inside an
    // expand block to avoid deleting next nodes
    const completeDeletionInsideExpandBlock = deleteInsideExpandBlock(editor)

    if (completeDeletionInsideExpandBlock) {
      return
    }

    const parentPath = Path.parent(editor.selection.anchor.path)
    const parentNode = Node.get(editor, parentPath) as TExpandBlockTitle
    const isActionBlockHandled = handleActionBlock(editor, parentNode, 'forward')

    if (isActionBlockHandled) {
      return
    }

    const completeDeletionForward = deleteForwardHelper(editor)

    if (completeDeletionForward) {
      return
    }

    deleteForward(unit)
  }

  // if prev node is a void node, remove the current node and select the void node
  editor.deleteBackward = (unit) => {
    if (
      !editor.selection ||
      !Range.isCollapsed(editor.selection) ||
      editor.selection.anchor.offset !== 0
    ) {
      return deleteBackward(unit)
    }

    // The method is necessary to remove the wrapper of elements of type lists/pre/code
    // when they are the first elements in the editor
    const completeUnwrapBlocks = unwrapBlocks(editor)

    // if we are in a table and we are deleting the contents inside the table cell, then this method
    // will avoid deleting the entire table and will delete only its contents
    const completeDeletionInTable = behaviorForTable(editor)

    const parentPath = Path.parent(editor.selection.anchor.path)
    const parentNode = Node.get(editor, parentPath) as TExpandBlockTitle
    const parentIsEmpty = Node.string(parentNode).length === 0

    // before removing the line with marginLeft, first remove the margin
    if (
      (parentNode as TCustomElement).type === BLOCK_FORMATS.paragraph &&
      parentNode['marginLeft']
    ) {
      return Transforms.setNodes(editor, { marginLeft: '' })
    }

    // when deleting an expandBlock, part of the markup remains, this method helps
    // to remove the entire expandBlock
    const completeDeletionInExpandBlock = removeExpandBlock(editor, parentNode)

    const isActionBlockHandled = handleActionBlock(editor, parentNode, 'backward')

    if (
      completeDeletionInTable ||
      completeDeletionInExpandBlock ||
      completeUnwrapBlocks ||
      isActionBlockHandled
    ) {
      return
    }

    if (parentIsEmpty && Path.hasPrevious(parentPath)) {
      const prevNodePath = Path.previous(parentPath)
      const prevNode = Node.get(editor, prevNodePath) as TCustomElement

      //if the expandBlock is closed, then before deleting the content,
      //it will reveal it to us so that we can see what we are deleting
      if (prevNode.type === BLOCK_FORMATS.expandBlock && prevNode['isOpen'] === false) {
        updateExpandBlock(editor, prevNodePath, true)
      }

      if (prevNode.type === BLOCK_FORMATS.figure && prevNode.children.length === 1) {
        Transforms.removeNodes(editor, { at: prevNodePath })
      }

      if (Editor.isVoid(editor, prevNode)) {
        return Transforms.removeNodes(editor)
      }
    }

    deleteBackward(unit)
  }

  return editor
}
